// Teiger (AOR / geogjerder) i COP — nå på DELT kontrakt med GNÅ.
//
// Før: verktøyet hadde sitt eget `Geofence`-begrep (kun polygon, `[lon,lat]`,
// `hostile | any`) og holdt sonene i et privat array. Trykket operatøren F5, var
// sonen borte. Ingen annen operatør hadde sett den. GNÅ hadde aldri hørt om den.
//
// Nå: alt går gjennom `@skade/contracts/geofence` (samme skjema, samme
// reduksjon, samme punkt-i-teig-test som appen), sonene ligger i hubben, og
// GNÅ-teiger vises her mens SKADE-teiger pushes ut til telefonene. Verktøyet
// eier bare RENDERING og lokal enter/exit-deteksjon mot spor — sannheten ligger
// i hubben.
import type { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import {
  punktITeig, teigUtloser, teigTilGeoJson, teigSentroide, teigFraRing,
  reduserTeiger, teigErGyldig,
  type Teig, type TeigHendelse, type TeigGjelderFor, type TeigRetning, type Track,
} from '@skade/contracts';

export type { Teig } from '@skade/contracts';

export type GeofenceEvent = { fence: Teig; track: Track; kind: 'enter' | 'exit' };

/** Debounce per (teig, spor, retning) slik at et spor på grensa ikke spammer. */
const DEBOUNCE_MS = 5000;

export class GeofenceTool {
  /** Hendelseslogg — reduseres til gjeldende sett, som i GNÅ og i hubben. */
  private hendelser: TeigHendelse[] = [];
  private teiger: Teig[] = [];
  private membership = new Map<string, Set<string>>(); // teigId → sporId inne
  private lastFire = new Map<string, number>();

  constructor(
    private map: MlMap,
    private onEvent: (e: GeofenceEvent) => void,
    /** Kalles når operatøren lager/endrer/sletter en teig, så main.ts kan sende
     * den til hubben. Uten denne er verktøyet lokalt (og sier det ikke). */
    private onLocalChange?: (h: TeigHendelse) => void,
  ) {
    map.on('load', () => this.initLayers());
  }

  private initLayers() {
    if (this.map.getSource('geofence')) return;
    this.map.addSource('geofence', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    this.map.addLayer({
      id: 'geofence-fill', type: 'fill', source: 'geofence',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': ['case', ['==', ['get', 'kilde'], 'gna'], '#5b9bd5', '#8ba888'],
        'fill-opacity': 0.1,
      },
    });
    this.map.addLayer({
      id: 'geofence-line', type: 'line', source: 'geofence',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'line-color': ['case', ['==', ['get', 'kilde'], 'gna'], '#5b9bd5', '#8ba888'],
        'line-width': 1.5, 'line-dasharray': [3, 2],
      },
    });
    this.map.addLayer({
      id: 'geofence-label', type: 'symbol', source: 'geofence',
      filter: ['==', '$type', 'Point'], // kun sentroide-punktet (unngå dobbel etikett)
      layout: { 'text-field': ['get', 'label'], 'text-font': ['Open Sans Regular'], 'text-size': 12 },
      paint: { 'text-color': '#8ba888', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.5 },
    });
  }

  /**
   * Legger til en teig fra en COP-tegnet ring `[[lon,lat],…]`.
   * Returnerer teigen, eller null når ringen ikke holder som polygon.
   */
  add(
    ring: number[][],
    name: string,
    gjelderFor: TeigGjelderFor = 'alle',
    retninger: TeigRetning[] = ['inn', 'ut'],
  ): Teig | null {
    const nå = Date.now();
    const teig = teigFraRing(ring, {
      id: `gf-${nå}-${Math.floor(Math.random() * 1000)}`,
      name, gjelderFor, retninger, tsMs: nå,
    });
    if (!teig) return null;
    this.anvend({ op: 'upsert', teig });
    this.onLocalChange?.({ op: 'upsert', teig });
    return teig;
  }

  /** Sletter en teig lokalt og varsler kalleren (som sender til hubben). */
  remove(id: string): boolean {
    if (!this.teiger.some((t) => t.id === id)) return false;
    const h: TeigHendelse = { op: 'delete', id, tsMs: Date.now() };
    this.anvend(h);
    this.onLocalChange?.(h);
    return true;
  }

  /**
   * Tar imot en teig-hendelse UTENFRA (hub/GNÅ) uten å ekko den tilbake.
   * Dette er hvordan en teig en lagfører tegnet i appen dukker opp i COP.
   */
  ingest(h: TeigHendelse): void {
    this.anvend(h);
  }

  private anvend(h: TeigHendelse): void {
    this.hendelser.push(h);
    // Hold loggen begrenset: reduser til gjeldende sett og bygg loggen på nytt
    // fra det. Reduksjonen er idempotent, så dette taper ingen informasjon vi
    // trenger — bare historikk vi ikke bruker.
    if (this.hendelser.length > 2000) {
      this.hendelser = reduserTeiger(this.hendelser).map((teig) => ({ op: 'upsert' as const, teig }));
    }
    this.teiger = reduserTeiger(this.hendelser).filter(teigErGyldig);
    const levende = new Set(this.teiger.map((t) => t.id));
    for (const id of [...this.membership.keys()]) {
      if (!levende.has(id)) this.membership.delete(id);
    }
    for (const t of this.teiger) {
      if (!this.membership.has(t.id)) this.membership.set(t.id, new Set());
    }
    this.render();
  }

  count(): number { return this.teiger.length; }

  /** Gjeldende teiger (til paneler/lister). */
  snapshot(): Teig[] { return [...this.teiger]; }

  private render() {
    const features: unknown[] = [];
    for (const t of this.teiger) {
      const poly = teigTilGeoJson(t);
      if (poly) features.push(poly);
      const c = teigSentroide(t);
      if (c) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: c },
          // Etiketten sier hvem som eier teigen — TOC må se hva som kom fra felt.
          properties: {
            label: t.kilde === 'gna' ? `${t.name} (GNÅ)` : t.name,
            kilde: t.kilde,
          },
        });
      }
    }
    const src = this.map.getSource('geofence') as GeoJSONSource | undefined;
    src?.setData({ type: 'FeatureCollection', features } as never);
  }

  /** Kjøres ved hver spor-oppdatering. Debounce per (teig, spor, retning). */
  check(tracks: Track[]) {
    const now = Date.now();
    for (const t of this.teiger) {
      const inside = this.membership.get(t.id);
      if (!inside) continue;
      for (const spor of tracks) {
        const isIn = punktITeig(t, { lat: spor.lat, lon: spor.lon });
        const was = inside.has(spor.id);
        if (isIn === was) continue;
        if (isIn) inside.add(spor.id); else inside.delete(spor.id);
        const retning: TeigRetning = isIn ? 'inn' : 'ut';
        // Filteret ligger i kontrakten, ikke her: retning OG tilhørighet må
        // begge stemme. Medlemskapet oppdateres uansett, ellers ville et spor
        // som gikk inn «usett» aldri kunne utløse ut-varselet.
        if (!teigUtloser(t, spor.affiliation, retning)) continue;
        this.maybeFire(t, spor, isIn ? 'enter' : 'exit', now);
      }
    }
  }

  private maybeFire(fence: Teig, track: Track, kind: 'enter' | 'exit', now: number) {
    const key = `${fence.id}:${track.id}:${kind}`;
    if (now - (this.lastFire.get(key) ?? 0) < DEBOUNCE_MS) return;
    this.lastFire.set(key, now);
    this.onEvent({ fence, track, kind });
  }
}
